import { useMemo } from 'react';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { Box, Flex, Heading, Hide, Text } from '@qga/roo-ui/components';

import urlFor from '@shared/sanity/image/urlFor';
import { SCREEN_SIZE } from '@shared/v2/constants';
import { isCurrentlyWithinDateRangeUTC } from '@shared/v2/utils/date';

import { SecondaryPromo } from '@/v2/services/sanity/types/api/response/homepageHero';
import Badge from '../Badge';
import { SecondaryCTA } from '../CTA';
import Pricing from '../Pricing';

const BackgroundImage = styled.div`
  ${({
    mobileImage,
    desktopImage,
  }: {
    mobileImage: string;
    desktopImage: string;
  }) => {
    return css`
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      display: flex;
      flex-direction: row;
      background-image: url('${mobileImage}');
      background-repeat: no-repeat;
      height: 170px;
      border-top-right-radius: 0;
      border-top-left-radius: 0;
      width: 100%;
      background-size: cover;
      background-position: top;

      // Tablet and up
      ${SCREEN_SIZE.MD} {
        border-top-left-radius: 0;
        left: 390px;
        right: 0;
        height: auto;
      }

      // Desktop
      ${SCREEN_SIZE.LG} {
        background-size: auto;
        background-position: center;
        position: relative;
        height: 107px;
        width: 320px;
        left: auto;
        right: auto;
        background-image: url('${desktopImage}');
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
      }
    `;
  }}
`;

const Wrapper = styled(Flex)`
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  position: relative;
  border-radius: 10px;
  height: 100%;

  // Desktop
  ${SCREEN_SIZE.LG} {
    width: 320px;
    display: flex;
    margin-right: 8px;
  }
`;

const Main = styled(Flex)`
  border-top-right-radius: 52px;
  background: white;
  height: 100%;
  margin-top: 120px;
  z-index: 1;
  position: relative;

  // Tablet and up
  ${SCREEN_SIZE.MD} {
    position: initial;
    margin-top: 0;
  }

  ${SCREEN_SIZE.LG} {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
  }
`;

const SecondaryHero = ({
  callToAction,
  content,
  duration,
  headline,
  image,
  sash,
  tagline,
  ...rest
}: SecondaryPromo) => {
  const desktopImage = useMemo(
    () => urlFor(image.image).width(320).url(),
    [image.image],
  );
  const mobileImage = useMemo(
    () => urlFor(image.image).width(1100).url(),
    [image.image],
  );

  const isPromoActive = isCurrentlyWithinDateRangeUTC(
    duration.start.utc,
    duration.end.utc,
  );

  if (!isPromoActive) return null;

  const { variation, price, priceOverline, priceSubtext, priceSuffix } =
    content;
  const { link, label } = callToAction ?? {};
  const showTagline = !!(variation === 'taglineText' && tagline);
  const showPrice = !!(variation === 'price' && price);
  const showCta = !!(link && label);
  const isBadgeVisible = sash?.variation !== 'none';

  return (
    <Wrapper {...rest}>
      <BackgroundImage desktopImage={desktopImage} mobileImage={mobileImage} />
      <Main
        flexDirection="column"
        p={[4, 4, 6]}
        pt={[9, isBadgeVisible ? 9 : 6, 6]}
        width={['100%', '440px', '100%']}
      >
        <Hide>
          <Badge sash={sash} duration={duration} />
        </Hide>
        <Flex flexDirection="column" justifyContent="flex-start" flex={1}>
          <Heading.h2
            fontWeight={400}
            fontSize={['28px', '28px', '22px']}
            lineHeight={['40px', '40px', '33px']}
            letterSpacing={0}
            py={0}
            my={0}
          >
            {headline}
          </Heading.h2>
          {showTagline && (
            <Box mt={2}>
              <Text fontSize="sm" py={0} my={0}>
                {tagline}
              </Text>
            </Box>
          )}
          {showPrice && (
            <Box mt={2}>
              <Pricing
                price={price}
                priceOverline={priceOverline}
                priceSubtext={priceSubtext}
                priceSuffix={priceSuffix}
              />
            </Box>
          )}
        </Flex>
        {showCta && (
          <Box mt={4}>
            <SecondaryCTA
              href={link}
              variant="primary"
              data-testid="SECONDARY_HERO_CTA"
            >
              <Text fontWeight="bold" fontSize="sm" lineHeight="0">
                {label}
              </Text>
            </SecondaryCTA>
          </Box>
        )}
      </Main>
    </Wrapper>
  );
};

export default SecondaryHero;
